/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Modal from '../../../../components/Modal/Modal';
import { DangerButton, GhostButton } from '../../../../styles/ui/Button';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field } from '../../../../styles/forms/Field';
import { revokeCredential } from '../../../../services/finance/integrationCredentialsApi';
import { readApiError, financeErrorMessage } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// Revogação da credencial (handoff 0079 §3.3).
//
// O texto de confirmação é deliberadamente explícito sobre o que revogar NÃO
// faz: o usuário tende a achar que derruba as cobranças já emitidas. Não
// derruba — a chave segue válida na ASAAS até ser trocada lá, e webhooks e
// consultas do que já foi emitido continuam funcionando. O que a revogação
// bloqueia é a emissão NOVA por esta credencial.
//
// `reason` é opcional (3..255 no backend).
// ---------------------------------------------------------------------------

const Warning = styled.div`
   border: 1px solid rgba(133, 79, 11, 0.35);
   background: rgba(133, 79, 11, 0.08);
   color: #854f0b;
   border-radius: 10px;
   padding: 12px 14px;
   font-size: 12.5px;
   line-height: 1.55;
   margin-bottom: 14px;

   strong {
      font-weight: 800;
   }
`;

const FieldError = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.danger ?? '#b71c1c'};
   margin-top: 2px;
`;

export default function RevokeCredentialModal({
   isOpen,
   onClose,
   credential,
   onDone,
}) {
   const [reason, setReason] = useState('');
   const [fieldError, setFieldError] = useState(null);
   const [isSubmitting, setIsSubmitting] = useState(false);

   useEffect(() => {
      if (isOpen) {
         setReason('');
         setFieldError(null);
         setIsSubmitting(false);
      }
   }, [isOpen]);

   const handleConfirm = async () => {
      if (isSubmitting || !credential) return;

      // Motivo é opcional, mas se preenchido o backend exige 3..255.
      const trimmed = reason.trim();
      if (trimmed && trimmed.length < 3) {
         setFieldError('O motivo deve ter ao menos 3 caracteres.');
         return;
      }

      setIsSubmitting(true);
      setFieldError(null);
      try {
         const { message } = await revokeCredential(credential.id, trimmed);
         toast.success(message);
         onDone?.();
         onClose?.();
      } catch (err) {
         const { fieldErrors } = readApiError(err);
         if (fieldErrors.reason) {
            setFieldError(fieldErrors.reason);
         } else {
            toast.error(
               financeErrorMessage(err, 'Erro ao revogar a credencial.'),
            );
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const footer = (
      <>
         <GhostButton type="button" onClick={onClose} disabled={isSubmitting}>
            Cancelar
         </GhostButton>
         <DangerButton
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
         >
            {isSubmitting ? 'Revogando…' : 'Revogar credencial'}
         </DangerButton>
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title="Revogar credencial"
         maxWidth="520px"
         footer={footer}
      >
         <Warning>
            Revogar <strong>bloqueia a emissão de novas cobranças</strong> por
            esta credencial. As cobranças já emitidas{' '}
            <strong>continuam válidas</strong>: a chave segue funcionando na
            ASAAS até você trocá-la por lá, e as consultas e webhooks do que já
            existe seguem normais.
         </Warning>

         <FormGrid>
            <Field className="col-12">
               <label htmlFor="revoke-reason">Motivo (opcional)</label>
               <textarea
                  id="revoke-reason"
                  maxLength={255}
                  placeholder="Ex: chave comprometida, troca de conta, encerramento do CNPJ..."
                  value={reason}
                  onChange={(e) => {
                     setReason(e.target.value);
                     setFieldError(null);
                  }}
                  disabled={isSubmitting}
                  aria-invalid={!!fieldError || undefined}
               />
               {fieldError && <FieldError>{fieldError}</FieldError>}
            </Field>
         </FormGrid>
      </Modal>
   );
}

RevokeCredentialModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   credential: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   }),
   onDone: PropTypes.func,
};
